import {
  type Branch,
  type CreateUserInput,
  GRANTABLE_PERMISSIONS,
  type Page as PageDto,
  PERMISSION_LABELS,
  PERMISSIONS,
  type Permission,
  type Role,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  type UpdateUserInput,
  type User,
} from '@edms/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Plus, Search, UserCog, Users } from 'lucide-react';
import { useState } from 'react';

import { PageBody, PageHeader } from '@/components/page-header';
import { type Column, DataTable, Pagination } from '@/components/ui/data-table';
import { Dialog, useToast } from '@/components/ui/overlays';
import { Button, Checkbox, Field, Input, Select } from '@/components/ui/primitives';
import { Badge, EmptyState } from '@/components/ui/surfaces';
import { useAuth } from '@/features/auth/auth-context';
import { api, ApiError } from '@/lib/api-client';
import { formatRelative } from '@/lib/utils';

const ROLE_TONES = {
  ADMINISTRATOR: 'danger',
  AREA_MANAGER: 'accent',
  BRANCH_LEADER: 'info',
  BRANCH_ASSISTANT: 'neutral',
  VIEWER: 'neutral',
} as const;

/** Administration des comptes (§8). */
export function UsersPage(): React.JSX.Element {
  const { can } = useAuth();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<User | null>(null);
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState<User | null>(null);

  const { data: branches } = useQuery({
    queryKey: ['branches', 'selectable'],
    queryFn: () => api.get<Branch[]>('/branches/selectable'),
    staleTime: 5 * 60_000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['users', { search, page }],
    queryFn: () =>
      api.get<PageDto<User>>('/users', { page, pageSize: 25, search: search || undefined }),
    placeholderData: keepPreviousData,
  });

  const writable = can(PERMISSIONS.USERS_WRITE);

  const columns: readonly Column<User>[] = [
    {
      key: 'name',
      header: 'Utilisateur',
      render: (row) => (
        <div className="flex flex-col">
          <span className="text-sm font-medium text-ink">{row.fullName}</span>
          <span className="text-2xs text-ink-faint">{row.email}</span>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Rôle',
      width: '16%',
      render: (row) => <Badge tone={ROLE_TONES[row.role]}>{ROLE_LABELS[row.role]}</Badge>,
    },
    {
      key: 'branch',
      header: 'Bureau',
      width: '18%',
      render: (row) => (
        <div className="flex flex-col">
          <span className="text-sm text-ink-muted">
            <span className="font-mono text-2xs text-ink-faint">{row.branchCode}</span>{' '}
            {row.branchName}
          </span>
          {row.managedBranchIds.length > 0 && (
            <span className="text-2xs text-ink-faint">
              + {row.managedBranchIds.length} bureau(x) supervisé(s)
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'state',
      header: 'État',
      width: '14%',
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          <Badge tone={row.isActive ? 'success' : 'neutral'} dot>
            {row.isActive ? 'Actif' : 'Désactivé'}
          </Badge>
          {row.mustChangePassword && <Badge tone="warning">Mot de passe à renouveler</Badge>}
        </div>
      ),
    },
    {
      key: 'lastLogin',
      header: 'Dernière connexion',
      align: 'right',
      width: '14%',
      hideOnNarrow: true,
      render: (row) => <span className="text-xs text-ink-faint">{formatRelative(row.lastLoginAt)}</span>,
    },
    ...(writable
      ? [
          {
            key: 'actions',
            header: '',
            align: 'right' as const,
            width: '10%',
            render: (row: User) => (
              <div className="flex justify-end gap-1">
                <Button size="sm" variant="ghost" onClick={() => setResetting(row)} title="Réinitialiser le mot de passe">
                  <KeyRound className="size-3.5" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(row)} title="Modifier">
                  <UserCog className="size-3.5" />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <PageHeader
        title="Utilisateurs"
        description="Comptes, rôles et permissions individuelles"
        actions={
          writable && (
            <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
              Nouvel utilisateur
            </Button>
          )
        }
        toolbar={
          <Input
            size="sm"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Rechercher par nom ou e-mail…"
            leadingIcon={<Search className="size-4" />}
            className="max-w-sm"
            aria-label="Rechercher un utilisateur"
          />
        }
      />

      <PageBody>
        <div className="overflow-hidden rounded-lg border border-line-subtle bg-raised shadow-[var(--shadow-card)]">
          <DataTable
            columns={columns}
            rows={data?.items ?? []}
            rowKey={(row) => row.id}
            loading={isLoading}
            empty={<EmptyState icon={Users} title="Aucun utilisateur" />}
          />
          {data && (
            <Pagination
              page={data.page}
              pageSize={data.pageSize}
              total={data.total}
              totalPages={data.totalPages}
              onPageChange={setPage}
            />
          )}
        </div>
      </PageBody>

      {creating && (
        <UserDialog branches={branches ?? []} onClose={() => setCreating(false)} />
      )}
      {editing && (
        <UserDialog user={editing} branches={branches ?? []} onClose={() => setEditing(null)} />
      )}
      {resetting && <ResetPasswordDialog user={resetting} onClose={() => setResetting(null)} />}
    </>
  );
}

function UserDialog({
  user,
  branches,
  onClose,
}: {
  user?: User;
  branches: readonly Branch[];
  onClose: () => void;
}): React.JSX.Element {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [fullName, setFullName] = useState(user?.fullName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [role, setRole] = useState<Role>(user?.role ?? 'BRANCH_ASSISTANT');
  const [branchId, setBranchId] = useState(user?.branchId ?? branches[0]?.id ?? '');
  const [managedBranchIds, setManagedBranchIds] = useState<string[]>(user?.managedBranchIds ?? []);
  const [permissions, setPermissions] = useState<Permission[]>(user?.additionalPermissions ?? []);
  const [isActive, setIsActive] = useState(user?.isActive ?? true);
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const grantable = GRANTABLE_PERMISSIONS[role];

  const save = useMutation({
    mutationFn: () => {
      if (user) {
        const input: UpdateUserInput = {
          fullName,
          role,
          branchId,
          managedBranchIds: role === 'AREA_MANAGER' ? managedBranchIds : [],
          additionalPermissions: permissions.filter((permission) => grantable.includes(permission)),
          isActive,
        };
        return api.patch<User>(`/users/${user.id}`, input);
      }

      const input: CreateUserInput = {
        fullName,
        email,
        role,
        branchId,
        managedBranchIds: role === 'AREA_MANAGER' ? managedBranchIds : [],
        additionalPermissions: permissions.filter((permission) => grantable.includes(permission)),
        temporaryPassword,
      };
      return api.post<User>('/users', input);
    },
    onSuccess: () => {
      toast.success(user ? 'Utilisateur modifié' : 'Utilisateur créé');
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
    onError: (cause) => {
      setError(cause instanceof ApiError ? cause.message : 'Enregistrement impossible.');
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={user ? `Modifier ${user.fullName}` : 'Nouvel utilisateur'}
      description={ROLE_DESCRIPTIONS[role]}
      footer={
        <>
          <Button onClick={onClose}>Annuler</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            {user ? 'Enregistrer' : 'Créer le compte'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <p role="alert" className="rounded-md border border-danger-border bg-danger-soft px-3 py-2 text-xs text-danger">
            {error}
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nom complet" htmlFor="user-name" required>
            <Input id="user-name" value={fullName} onChange={(event) => setFullName(event.target.value)} />
          </Field>

          <Field label="Adresse e-mail" htmlFor="user-email" required>
            <Input
              id="user-email"
              type="email"
              value={email}
              disabled={Boolean(user)}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>

          <Field label="Rôle" htmlFor="user-role" required>
            <Select
              id="user-role"
              value={role}
              onChange={(event) => {
                setRole(event.target.value as Role);
                setPermissions([]);
              }}
            >
              {(Object.keys(ROLE_LABELS) as Role[]).map((value) => (
                <option key={value} value={value}>
                  {ROLE_LABELS[value]}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Bureau de rattachement"
            htmlFor="user-branch"
            required
            {...(role === 'VIEWER'
              ? {
                  hint: "Rattachement administratif seulement : ce profil consulte les reçus de tous les bureaux.",
                }
              : {})}
          >
            <Select id="user-branch" value={branchId} onChange={(event) => setBranchId(event.target.value)}>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.code} — {branch.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {!user && (
          <Field
            label="Mot de passe temporaire"
            htmlFor="user-password"
            required
            hint="Transmis hors ligne. L'utilisateur devra le renouveler à sa première connexion."
          >
            <Input
              id="user-password"
              type="text"
              value={temporaryPassword}
              onChange={(event) => setTemporaryPassword(event.target.value)}
              className="font-mono"
            />
          </Field>
        )}

        {role === 'AREA_MANAGER' && (
          <Field
            label="Bureaux supervisés"
            hint="En plus de son bureau de rattachement. Consultation seule."
          >
            <div className="grid max-h-40 gap-2 overflow-y-auto rounded-md border border-line-subtle p-3 sm:grid-cols-2">
              {branches
                .filter((branch) => branch.id !== branchId)
                .map((branch) => (
                  <Checkbox
                    key={branch.id}
                    label={`${branch.code} — ${branch.name}`}
                    checked={managedBranchIds.includes(branch.id)}
                    onChange={(event) =>
                      setManagedBranchIds((current) =>
                        event.target.checked
                          ? [...current, branch.id]
                          : current.filter((id) => id !== branch.id),
                      )
                    }
                  />
                ))}
            </div>
          </Field>
        )}

        {grantable.length > 0 && (
          <Field
            label="Permissions supplémentaires"
            hint="Seules les permissions compatibles avec le rôle sont proposées."
          >
            <div className="grid gap-2 rounded-md border border-line-subtle p-3 sm:grid-cols-2">
              {grantable.map((permission) => (
                <Checkbox
                  key={permission}
                  label={PERMISSION_LABELS[permission]}
                  checked={permissions.includes(permission)}
                  onChange={(event) =>
                    setPermissions((current) =>
                      event.target.checked
                        ? [...current, permission]
                        : current.filter((value) => value !== permission),
                    )
                  }
                />
              ))}
            </div>
          </Field>
        )}

        {user && (
          <Checkbox
            label="Compte actif"
            hint="Un compte désactivé ne peut plus se connecter ; ses sessions sont fermées immédiatement."
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
          />
        )}
      </div>
    </Dialog>
  );
}

function ResetPasswordDialog({ user, onClose }: { user: User; onClose: () => void }): React.JSX.Element {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = useMutation({
    mutationFn: () => api.post(`/users/${user.id}/reset-password`, { temporaryPassword: password }),
    onSuccess: () => {
      toast.success('Mot de passe réinitialisé', 'Toutes les sessions de cet utilisateur sont fermées.');
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : 'Réinitialisation impossible.'),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Réinitialiser le mot de passe de ${user.fullName}`}
      description="Le compte devra définir un nouveau mot de passe à sa prochaine connexion."
      footer={
        <>
          <Button onClick={onClose}>Annuler</Button>
          <Button variant="primary" loading={reset.isPending} onClick={() => reset.mutate()}>
            Réinitialiser
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && (
          <p role="alert" className="rounded-md border border-danger-border bg-danger-soft px-3 py-2 text-xs text-danger">
            {error}
          </p>
        )}
        <Field label="Mot de passe temporaire" htmlFor="reset-password" required>
          <Input
            id="reset-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="font-mono"
            autoFocus
          />
        </Field>
      </div>
    </Dialog>
  );
}
